// src/frontend/src/pages/ResourcePage.tsx
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';

interface ResourceContent {
  title: string;
  subtitle: string;
  description: string;
  resourceType: 'guide' | 'checklist' | 'template' | 'calculator' | 'assessment';
  sections: {
    title: string;
    content: string[];
    tips?: string[];
  }[];
  downloadable?: {
    title: string;
    description: string;
    format: string;
  };
  relatedResources: {
    title: string;
    description: string;
    link: string;
  }[];
  ctaTitle: string;
  ctaDescription: string;
}

const resourceData: Record<string, ResourceContent> = {
  'pm-best-practices': {
    title: 'Project Management Best Practices',
    subtitle: 'Essential practices every PM should master',
    description: 'A comprehensive guide to the fundamental practices that separate successful project managers from those who struggle. Based on analysis of 1000+ successful projects.',
    resourceType: 'guide',
    sections: [
      {
        title: 'Stakeholder Communication',
        content: [
          'Establish regular communication cadence with all stakeholders',
          'Create stakeholder communication plans with preferred channels and frequency',
          'Use the "no surprises" principle - communicate problems early',
          'Tailor your communication style to each stakeholder\'s preferences'
        ],
        tips: [
          'Weekly stakeholder updates prevent 80% of escalations',
          'Use visual status reports for executives (they prefer dashboards)',
          'Always include next steps and required decisions in communications'
        ]
      },
      {
        title: 'Scope Management',
        content: [
          'Document scope clearly in terms stakeholders understand',
          'Implement formal change request processes',
          'Educate stakeholders on the impact of scope changes',
          'Create a change impact assessment template'
        ],
        tips: [
          'Scope creep kills more projects than technical issues',
          'Always quantify the impact of changes in time and budget',
          'Use "scope, time, budget - pick two" principle for trade-off discussions'
        ]
      },
      {
        title: 'Risk Management',
        content: [
          'Maintain living risk registers with mitigation plans',
          'Conduct weekly risk assessments with the team',
          'Communicate top 3 risks to stakeholders regularly',
          'Plan for known unknowns and unknown unknowns'
        ],
        tips: [
          'Risks identified early cost 10x less to mitigate',
          'Include team members in risk identification - they see things you miss',
          'Always have contingency plans for your top 3 risks'
        ]
      },
      {
        title: 'Team Leadership',
        content: [
          'Hold effective team meetings with clear agendas',
          'Provide psychological safety for team members to voice concerns',
          'Remove blockers quickly and escalate when necessary',
          'Celebrate wins and learn from failures together'
        ],
        tips: [
          'Team motivation impacts delivery speed more than technical skills',
          'One-on-ones with team members prevent most people issues',
          'Recognition should be specific, timely, and public when appropriate'
        ]
      }
    ],
    relatedResources: [
      {
        title: 'Stakeholder Management Guide',
        description: 'Deep dive into managing different stakeholder types',
        link: '/resources/stakeholder-management-guide'
      },
      {
        title: 'PM Communication Templates',
        description: 'Ready-to-use email templates and scripts',
        link: '/resources/pm-communication-templates'
      },
      {
        title: 'Project Risk Assessment',
        description: 'Free tool to assess your project risks',
        link: '/resources/pm-skills-assessment'
      }
    ],
    ctaTitle: 'Practice These Best Practices',
    ctaDescription: 'Use PM Coach to practice implementing these best practices in realistic scenarios.'
  },
  'stakeholder-management-guide': {
    title: 'Complete Stakeholder Management Guide',
    subtitle: 'Master the art of stakeholder relationships',
    description: 'Everything you need to know about identifying, analyzing, and managing stakeholders throughout your project lifecycle.',
    resourceType: 'guide',
    sections: [
      {
        title: 'Stakeholder Identification',
        content: [
          'Map all individuals and groups affected by or affecting your project',
          'Include internal and external stakeholders',
          'Consider secondary stakeholders who have indirect influence',
          'Update stakeholder lists as projects evolve'
        ],
        tips: [
          'Start stakeholder mapping in project initiation phase',
          'Ask "Who else cares about this outcome?" to find hidden stakeholders',
          'Include people who can block your project, even if they seem unrelated'
        ]
      },
      {
        title: 'Stakeholder Analysis',
        content: [
          'Assess each stakeholder\'s level of interest and influence',
          'Understand their goals, motivations, and concerns',
          'Identify their preferred communication styles',
          'Map relationships between stakeholders'
        ],
        tips: [
          'High influence + low interest stakeholders need to be kept satisfied',
          'High interest + low influence stakeholders want to be kept informed',
          'High influence + high interest stakeholders require active management'
        ]
      },
      {
        title: 'Engagement Strategies',
        content: [
          'Develop tailored communication approaches for each stakeholder',
          'Create regular touchpoints and feedback loops',
          'Build trust through consistent delivery and transparency',
          'Address concerns proactively before they become problems'
        ],
        tips: [
          'Match communication frequency to stakeholder\'s role and interest level',
          'Use their preferred communication channels (email, Slack, in-person)',
          'Always follow up on commitments made to stakeholders'
        ]
      },
      {
        title: 'Difficult Situations',
        content: [
          'Handle conflicting stakeholder requirements diplomatically',
          'Manage expectations when you need to say "no"',
          'Escalate appropriately when stakeholder conflicts arise',
          'Turn adversarial relationships into collaborative partnerships'
        ],
        tips: [
          'Focus on underlying interests, not stated positions',
          'Find win-win solutions whenever possible',
          'Use data and business impact to support difficult decisions'
        ]
      }
    ],
    downloadable: {
      title: 'Stakeholder Analysis Template',
      description: 'Excel template for mapping and analyzing your project stakeholders',
      format: 'Excel (.xlsx)'
    },
    relatedResources: [
      {
        title: 'PM Best Practices',
        description: 'Fundamental practices every PM should master',
        link: '/resources/pm-best-practices'
      },
      {
        title: 'Scope Creep Management',
        description: 'Learn to handle changing requirements',
        link: '/solutions/scope-creep-management'
      },
      {
        title: 'Executive Communication',
        description: 'Master managing up to executives',
        link: '/solutions/executive-communication'
      }
    ],
    ctaTitle: 'Practice Stakeholder Conversations',
    ctaDescription: 'Put this guide into action with realistic stakeholder simulations.'
  },
  'pm-skills-assessment': {
    title: 'PM Skills Assessment',
    subtitle: 'Discover your PM strengths and growth areas',
    description: 'Free assessment that evaluates your project management competencies across 8 key areas and provides personalized development recommendations.',
    resourceType: 'assessment',
    sections: [
      {
        title: 'Assessment Areas',
        content: [
          'Stakeholder Communication & Management',
          'Project Planning & Execution',
          'Risk Management & Problem Solving',
          'Team Leadership & Motivation',
          'Scope & Change Management',
          'Budget & Resource Management',
          'Quality & Delivery Management',
          'Strategic Thinking & Business Acumen'
        ]
      },
      {
        title: 'How It Works',
        content: [
          'Complete 40 scenario-based questions (15 minutes)',
          'Receive detailed scoring across all 8 competency areas',
          'Get personalized learning recommendations',
          'Compare your scores to PM industry benchmarks',
          'Download a development plan tailored to your results'
        ]
      },
      {
        title: 'What You\'ll Learn',
        content: [
          'Your top 3 PM strengths to leverage',
          'Areas where you need the most development',
          'Specific skills to focus on for career advancement',
          'Recommended PM Coach learning tracks for your level',
          'Industry benchmarks for your experience level'
        ]
      }
    ],
    relatedResources: [
      {
        title: 'New PM Solutions',
        description: 'Coaching designed for new project managers',
        link: '/solutions/new-project-managers'
      },
      {
        title: 'Senior PM Challenges',
        description: 'Advanced scenarios for experienced PMs',
        link: '/solutions/senior-project-managers'
      },
      {
        title: 'Learning Tracks',
        description: 'Structured learning paths based on your assessment',
        link: '/learning-tracks'
      }
    ],
    ctaTitle: 'Take Your Free Assessment',
    ctaDescription: 'Discover your PM strengths and get a personalized development plan.'
  },
  // Add to resourceData in src/frontend/src/pages/ResourcePage.tsx
'pm-career-guide': {
  title: 'Complete PM Career Progression Guide',
  subtitle: 'Navigate your project management career from entry-level to executive',
  description: 'A comprehensive roadmap for advancing your project management career. Learn the skills, competencies, and stakeholder management capabilities needed at each career stage, from Associate PM to VP of Product.',
  resourceType: 'guide',
  sections: [
    {
      title: 'Entry-Level PM (0-2 years)',
      content: [
        'Master fundamental PM processes and methodologies',
        'Build credibility with team members and immediate stakeholders',
        'Learn to facilitate effective meetings and status updates',
        'Develop clear communication and documentation skills',
        'Practice saying "no" diplomatically when scope creeps',
        'Build relationships with key stakeholders'
      ],
      tips: [
        'Focus on execution excellence before trying to be strategic',
        'Ask lots of questions - curiosity is your superpower early in your career',
        'Document everything and create clear paper trails',
        'Volunteer for cross-functional projects to build your network'
      ]
    },
    {
      title: 'Mid-Level PM (2-5 years)',
      content: [
        'Take ownership of complex, multi-stakeholder projects',
        'Develop strategic thinking and business acumen',
        'Master difficult stakeholder conversations and conflict resolution',
        'Learn to influence without authority across departments',
        'Build expertise in specific domains or industries',
        'Start mentoring junior PMs and sharing knowledge'
      ],
      tips: [
        'Begin thinking about project outcomes, not just outputs',
        'Develop your personal brand and areas of expertise',
        'Network strategically within your industry',
        'Start speaking at conferences and writing about PM topics'
      ]
    },
    {
      title: 'Senior PM (5-8 years)',
      content: [
        'Lead strategic initiatives and organizational transformation',
        'Manage complex stakeholder ecosystems and executive relationships',
        'Drive consensus building across competing business priorities',
        'Develop other PMs and build PM capability within the organization',
        'Shape project management standards and processes',
        'Navigate organizational politics and drive change management'
      ],
      tips: [
        'Focus on business impact and revenue generation',
        'Build strong relationships with executives and C-level stakeholders',
        'Develop thought leadership in your domain',
        'Consider pursuing advanced certifications like PfMP or executive education'
      ]
    },
    {
      title: 'Principal/Lead PM (8-12 years)',
      content: [
        'Set strategic direction for multiple product lines or portfolios',
        'Manage and develop teams of senior PMs',
        'Shape organizational PM practices and culture',
        'Partner with executives on business strategy and planning',
        'Drive organizational change and digital transformation',
        'Represent the organization at industry events and with partners'
      ],
      tips: [
        'Balance strategic thinking with hands-on leadership',
        'Invest heavily in developing your team and succession planning',
        'Build external networks and industry relationships',
        'Consider board advisor or consulting opportunities'
      ]
    },
    {
      title: 'Director/VP Level (12+ years)',
      content: [
        'Define organizational PM strategy and vision',
        'Manage large portfolios of projects and programs',
        'Build and scale PM organizations and capabilities',
        'Partner with C-suite on business strategy and execution',
        'Drive cultural change and organizational transformation',
        'Represent the organization externally and build strategic partnerships'
      ],
      tips: [
        'Focus on organizational outcomes and cultural transformation',
        'Develop exceptional communication and presentation skills',
        'Build strategic partnerships both internally and externally',
        'Consider pursuing executive education at top business schools'
      ]
    }
  ],
  relatedResources: [
    {
      title: 'PM Skills Assessment',
      description: 'Evaluate your current PM competency level',
      link: '/resources/pm-skills-assessment'
    },
    {
      title: 'New PM Solutions',
      description: 'Coaching designed specifically for new project managers',
      link: '/solutions/new-project-managers'
    },
    {
      title: 'Senior PM Challenges',
      description: 'Advanced scenarios for experienced PMs',
      link: '/solutions/senior-project-managers'
    }
  ],
  ctaTitle: 'Accelerate Your PM Career Growth',
  ctaDescription: 'Practice the stakeholder conversations that define success at your career level.'
}
  // Add more resource data as needed
};

const ResourcePage: React.FC = () => {
  const { resourceType } = useParams<{ resourceType: string }>();
  
  if (!resourceType || !resourceData[resourceType]) {
    return (
      <HomeLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Resource Not Found</h1>
          <p className="text-gray-600 mb-8">The resource you're looking for doesn't exist.</p>
          <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Back to Home
          </Link>
        </div>
      </HomeLayout>
    );
  }

  const resource = resourceData[resourceType];

  const getResourceIcon = () => {
    switch (resource.resourceType) {
      case 'guide': return '📖';
      case 'checklist': return '✅';
      case 'template': return '📄';
      case 'calculator': return '🧮';
      case 'assessment': return '📊';
      default: return '📚';
    }
  };

  return (
    <HomeLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="py-16">
          <div className="text-center mb-12">
            <div className="text-6xl mb-6">{getResourceIcon()}</div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6">
              {resource.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {resource.subtitle}
            </p>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              {resource.description}
            </p>
          </div>

          {/* Download CTA for downloadable resources */}
          {resource.downloadable && (
            <div className="max-w-2xl mx-auto bg-blue-50 border border-blue-200 rounded-lg p-6 mb-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  Download: {resource.downloadable.title}
                </h3>
                <p className="text-blue-700 mb-4">{resource.downloadable.description}</p>
                <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                  Download {resource.downloadable.format}
                </button>
              </div>
            </div>
          )}

          {/* Assessment CTA for assessment type */}
          {resource.resourceType === 'assessment' && (
            <div className="max-w-2xl mx-auto bg-green-50 border border-green-200 rounded-lg p-6 mb-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Take Your Free Assessment
                </h3>
                <p className="text-green-700 mb-4">15 minutes • Instant results • Personalized recommendations</p>
                <Link
                  to="/register"
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors inline-block"
                >
                  Start Assessment
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Sections */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto">
            {resource.sections.map((section, index) => (
              <div key={index} className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">{section.title}</h2>
                
                {/* Content List */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <ul className="space-y-4">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <svg className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 text-lg">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tips Section */}
                {section.tips && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" clipRule="evenodd" />
                      </svg>
                      Pro Tips
                    </h3>
                    <ul className="space-y-2">
                      {section.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start text-yellow-700">
                          <span className="font-bold mr-2">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Related Resources */}
        <div className="py-16 bg-gray-50 rounded-2xl">
          <div className="max-w-6xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Related Resources
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {resource.relatedResources.map((related, index) => (
                <Link
                  key={index}
                  to={related.link}
                  className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{related.title}</h3>
                  <p className="text-gray-600 mb-4">{related.description}</p>
                  <div className="flex items-center text-blue-600">
                    <span className="text-sm font-medium">Learn more</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{resource.ctaTitle}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {resource.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100"
            >
              Start Free Trial
            </Link>
            <Link
              to="/learning-tracks"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600"
            >
              View Learning Tracks
            </Link>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            14-day free trial • No credit card required • 20 coaching sessions included
          </p>
        </div>
      </div>
    </HomeLayout>
  );
};

export default ResourcePage;